import {
  Box,
  FormControl,
  Grid2 as Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material'
import {
  XRayDiagnosticResultCard,
  XRayDiagnosticResultModal
} from './components'
import { ExamSubtype, IExamResponse, IRelatedExam } from 'types/exam/types'
import { useMemo, useState } from 'react'
import { WarningAmber } from '@mui/icons-material'
import { CXRAY_SUBTYPE_OPTIONS } from 'pages/Diagnostics/constants'
import { useOrganHighlights } from './useOrganHighlights'
import { patchExam } from 'services/exam/examService'
import { useToast } from 'services/context/toast'
import analytics from 'services/analytics'
import { useSuperAdmin } from 'components/rules/PermissionByRole'

interface XRayExamProps {
  examData: IExamResponse
  /** Called after a card's subtype is successfully re-marked, so the parent can update state. */
  onSubtypeUpdated?: (examId: string, subtype: ExamSubtype) => void
}

interface SubtypeSelectProps {
  examId: string
  value?: ExamSubtype | null
  onUpdated?: (examId: string, subtype: ExamSubtype) => void
}

/** Dropdown to re-mark the subtype of a single original image. */
const SubtypeSelect = ({ examId, value, onUpdated }: SubtypeSelectProps) => {
  const [saving, setSaving] = useState(false)
  const toast = useToast()

  const handleChange = async (event: SelectChangeEvent) => {
    const newSubtype = event.target.value as ExamSubtype
    if (!newSubtype || newSubtype === value) return

    setSaving(true)
    const response = await patchExam(examId, { subtype: newSubtype })
    setSaving(false)

    if (response.success) {
      analytics.track('xray_subtype_changed', {
        from: value ?? null,
        to: newSubtype
      })
      onUpdated?.(examId, newSubtype)
      toast.handler({
        severity: 'success',
        content: 'Subtipo atualizado com sucesso'
      })
    } else {
      toast.handler({
        severity: 'error',
        content: 'Erro ao atualizar o subtipo'
      })
    }
  }

  return (
    <FormControl
      size='small'
      fullWidth
      sx={{ mt: 1 }}
      // Avoid triggering the card's onClick (which opens the modal).
      onClick={event => event.stopPropagation()}
    >
      <InputLabel id={`subtype-label-${examId}`}>Subtipo</InputLabel>
      <Select
        labelId={`subtype-label-${examId}`}
        label='Subtipo'
        value={value ?? ''}
        onChange={handleChange}
        disabled={saving}
      >
        {CXRAY_SUBTYPE_OPTIONS.map(option => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

/**
 * Coluna de cada lateral: largura ligeiramente menor que a original ('large' = 500px),
 * mantendo a AP/PA como foco e centralizando verticalmente em telas md+.
 */
const LATERAL_COLUMN_SX = {
  width: { xs: 500, md: 400 },
  alignSelf: { md: 'center' }
}

/** O card interno ocupa a largura da coluna lateral. */
const LATERAL_CARD_SX = {
  width: '100%'
}

interface SelectedImage {
  image: string
  title: string
  imageId: string
}

/** Picks the most recent related exam of a given subtype (list comes ordered desc). */
const pickLateral = (
  relatedExams: IRelatedExam[] | undefined,
  subtype: 'cxray_l_lat' | 'cxray_r_lat'
): IRelatedExam | undefined =>
  relatedExams?.find(exam => exam.subtype === subtype)

export const XRayExam = ({ examData, onSubtypeUpdated }: XRayExamProps) => {
  const [selectedImage, setSelectedImage] = useState<SelectedImage | null>(null)

  const theme = useTheme()
  // Re-marking the exam subtype is restricted to super admins.
  const isSuperAdmin = useSuperAdmin()

  const leftLateral = useMemo(
    () => pickLateral(examData.related_exams, 'cxray_l_lat'),
    [examData.related_exams]
  )
  const rightLateral = useMemo(
    () => pickLateral(examData.related_exams, 'cxray_r_lat'),
    [examData.related_exams]
  )

  const organHighlights = useOrganHighlights(examData)

  const isOriginalSelected = selectedImage?.imageId === 'Original'

  return (
    <Grid size={12}>
      <Typography
        variant='h6'
        textAlign='center'
        sx={{
          mb: 2,
          color: 'var(--primarySoft-button-color)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 2
        }}
      >
        Radiografia de Toráx
        <Tooltip
          title={
            <Typography variant='body2'>
              A responsabilidade pelo envio do tipo de exame correto é do
              hospital. A swissanalytica/sentinela se isenta de qualquer erro de
              análise resultante de radiografias que não sejam de tórax AP/PA.
              <br />
              <br />
              <b>Radiografia de tórax AP/PA:</b> exame de raios X que deve
              incluir todo o tórax, desde os ápices pulmonares até os ângulos
              costofrênicos, abrangendo ambos os pulmões, mediastino, coração e
              estruturas ósseas visíveis. Pode ser adquirida na projeção PA
              (posteroanterior), ou AP (anteroposterior).
            </Typography>
          }
          placement='top-start'
          PopperProps={{
            modifiers: [
              {
                name: 'preventOverflow',
                options: { padding: 8 }
              },
              {
                name: 'flip',
                enabled: true
              }
            ]
          }}
          slotProps={{
            popper: {
              sx: {
                '& .MuiTooltip-tooltip': {
                  maxWidth: 400,
                  padding: '8px 12px',
                  backgroundColor: theme.palette.warning.main,
                  color: theme.palette.warning.contrastText,
                  fontSize: '0.7rem'
                }
              }
            }
          }}
        >
          <IconButton
            size='small'
            sx={{
              color: 'warning.main',
              alignSelf: 'flex-start'
            }}
          >
            <WarningAmber />
          </IconButton>
        </Tooltip>
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          justifyContent: 'center',
          alignItems: 'flex-start',
          gap: 2,
          margin: '0 auto'
        }}
      >
        {/* Lateral Esquerdo (cxray_l_lat) — à esquerda */}
        {leftLateral && (
          <Box sx={LATERAL_COLUMN_SX}>
            <XRayDiagnosticResultCard
              title='Lateral Esquerdo'
              image={leftLateral.link_original_data}
              size='large'
              sx={LATERAL_CARD_SX}
              hideResult
              onClick={() =>
                setSelectedImage({
                  image: leftLateral.link_original_data,
                  title: 'Lateral Esquerdo',
                  imageId: 'LeftLateral'
                })
              }
            />
            {isSuperAdmin && (
              <SubtypeSelect
                examId={leftLateral.id_exam}
                value={leftLateral.subtype}
                onUpdated={onSubtypeUpdated}
              />
            )}
          </Box>
        )}

        {/* Imagem Original (AP/PA) — centro */}
        <Box sx={{ width: 500 }}>
          <XRayDiagnosticResultCard
            title='(AP/PA) Imagem Original'
            image={examData.link_original_data}
            size='large'
            hideResult
            onClick={() =>
              setSelectedImage({
                image: examData.link_original_data,
                title: '(AP/PA) Imagem Original',
                imageId: 'Original'
              })
            }
          />
          {isSuperAdmin && (
            <SubtypeSelect
              examId={examData.id_exam}
              value={examData.subtype}
              onUpdated={onSubtypeUpdated}
            />
          )}
        </Box>

        {/* Lateral Direito (cxray_r_lat) — à direita */}
        {rightLateral && (
          <Box sx={LATERAL_COLUMN_SX}>
            <XRayDiagnosticResultCard
              title='Lateral Direito'
              image={rightLateral.link_original_data}
              size='large'
              sx={LATERAL_CARD_SX}
              hideResult
              onClick={() =>
                setSelectedImage({
                  image: rightLateral.link_original_data,
                  title: 'Lateral Direito',
                  imageId: 'RightLateral'
                })
              }
            />
            {isSuperAdmin && (
              <SubtypeSelect
                examId={rightLateral.id_exam}
                value={rightLateral.subtype}
                onUpdated={onSubtypeUpdated}
              />
            )}
          </Box>
        )}
      </Box>

      <XRayDiagnosticResultModal
        title={selectedImage?.title ?? ''}
        image={selectedImage?.image ?? ''}
        imageId={selectedImage?.imageId ?? 'Original'}
        isOpen={selectedImage !== null}
        onClose={() => setSelectedImage(null)}
        hideResult
        showArrowButtons={false}
        overlay={isOriginalSelected ? organHighlights.overlay : undefined}
        sidePanel={isOriginalSelected ? organHighlights.sidePanel : undefined}
      />
    </Grid>
  )
}
export default XRayExam
